import { useState, useEffect, useCallback } from 'react'
import { marketingApi } from '../api/marketing'
import Boton from '../components/Boton'
import { useAuth } from '../context/AuthContext'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { colors, text, space, radius, sx } from '../design/tokens'

/**
 * MARKETING → Recompra (Martin, 18 oct 2026 — docs/diseno-recompra-secuencia-dormidos.md):
 * al cliente que no compra hace 30 días le llegan hasta diez mails, uno por
 * semana y cada uno un día distinto de la semana. Desde el 19 oct cada mail
 * sale por una señal real sobre lo que compró; sin señal, no sale.
 *
 * El tablero (Martin, 19 oct): arriba los números (mails, clientes, ventas),
 * los mails por día, cuántos mails recibió cada cliente, el listado de
 * clientes con lo que recibió cada uno, y abajo las herramientas para ver los
 * mails antes de mandarlos (estilo, firma de la carta, muestra).
 */

const VISTAS = [
  { id: '', label: 'Todos' },
  { id: 'activa', label: 'Recibiendo mails' },
  { id: 'volvio', label: 'Volvieron a comprar' },
  { id: 'sin_respuesta', label: 'Terminaron sin comprar' },
  { id: 'cortada', label: 'Cortados' },
  { id: 'control', label: 'Grupo de control' },
]

const ESTADO = {
  activa: { label: 'Recibiendo mails', bg: colors.infoBg, fg: colors.info },
  volvio: { label: 'Volvió a comprar', bg: colors.okBg, fg: colors.ok },
  sin_respuesta: { label: 'Terminó sin comprar', bg: colors.bgSoft, fg: colors.textSub },
  terminada: { label: 'Control terminado', bg: colors.bgSoft, fg: colors.textSub },
  cortada: { label: 'Cortado', bg: colors.warnBg, fg: colors.warn },
}

const DIAS = { 1: 'Lunes', 2: 'Martes', 3: 'Miércoles', 4: 'Jueves', 5: 'Viernes', 6: 'Sábado', 7: 'Domingo' }

const pct = (v) => (v == null ? '—' : `${String(v).replace('.', ',')} %`)
const tasa = (a, b) => (b ? pct(Math.round((1000 * a) / b) / 10) : '—')
const pesos = (n) => `$ ${fmtMonto(n, 0)}`
const diaCorto = (iso) => { const [, m, d] = iso.split('-'); return `${d}/${m}` }

export default function MktRecompraPage() {
  const { hasAnyRole } = useAuth()
  const esAdmin = hasAnyRole(['administrador'])
  const [vista, setVista] = useStoredState('lgr.filtros.recompra', '')
  const [familiaFiltro, setFamiliaFiltro] = useStoredState('lgr.filtros.recompra-familia', '')
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  const [aviso, setAviso] = useState(null)
  const [ocupado, setOcupado] = useState(false)
  const [confirmar, setConfirmar] = useState(false)
  const [cupo, setCupo] = useState('')
  const [prevCliente, setPrevCliente] = useState('')
  const [clienteBuscado, setClienteBuscado] = useState('')
  const [prevEstilo, setPrevEstilo] = useState('')
  const [preview, setPreview] = useState(null)
  const [prevError, setPrevError] = useState(null)
  const [senalVista, setSenalVista] = useState(0)
  const [firmaNombre, setFirmaNombre] = useState(null)
  const [firmaEmail, setFirmaEmail] = useState(null)

  const cargar = useCallback(async () => {
    try {
      const r = await marketingApi.recompra({ estado: vista || undefined, familia: familiaFiltro || undefined })
      setData(r.data)
      setError(false)
    } catch { setError(true) }
  }, [vista, familiaFiltro])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(cargar, 60000)

  const r = data?.resumen
  const activa = !!r?.activo_desde
  const familias = r?.familias ?? {}
  const senales = r?.senales ?? {}
  const estilos = r?.estilos ?? {}
  const estiloVista = prevEstilo || r?.estilo || 'carta'
  const t = r?.totales

  useEffect(() => {
    if (!r) return undefined
    let vivo = true
    marketingApi.recompraVistaPrevia(clienteBuscado, estiloVista)
      .then(({ data: d }) => { if (vivo) { setPreview(d); setPrevError(null); setSenalVista(n => Math.min(n, Math.max(0, d.senales.length - 1))) } })
      .catch((e) => { if (vivo) setPrevError(e.response?.data?.message || 'No se pudo armar la vista previa.') })
    return () => { vivo = false }
    // r cambia con cada refresco; alcanza con saber que ya cargó
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteBuscado, estiloVista, !!r])

  const configurar = async (body, textoOk) => {
    setOcupado(true); setAviso(null)
    try {
      await marketingApi.recompraConfigurar(body)
      setConfirmar(false)
      setAviso({ ok: true, texto: textoOk })
      cargar()
    } catch (e) {
      setAviso({ ok: false, texto: e.response?.data?.message || 'No se pudo guardar.' })
    } finally { setOcupado(false) }
  }

  const muestra = async () => {
    setOcupado(true); setAviso(null)
    try {
      const { data: d } = await marketingApi.recompraMuestra(preview?.cliente?.codigo_presea || clienteBuscado)
      setAviso({ ok: true, texto: d.mensaje })
    } catch (e) {
      setAviso({ ok: false, texto: e.response?.data?.message || 'No se pudo mandar la muestra.' })
    } finally { setOcupado(false) }
  }

  const filas = data?.data ?? []
  const diario = r?.diario ?? []
  const maxDia = Math.max(1, ...diario.map(d => d.mails))
  const embudo = r?.embudo_mails ?? []
  const base = embudo[0]?.clientes || 0
  const mails30 = diario.reduce((a, d) => a + d.mails, 0)

  return (
    <div>
      <div style={st.header}>
        <div>
          <h2 style={st.title}>Recompra</h2>
          <p style={st.sub}>
            Mails a los clientes que no compran hace 30 días: hasta diez, uno por semana. Cada mail le ofrece algo
            concreto de lo que compró (las añadas guardadas de su vino, lo nuevo de su bodega o de su rubro, su
            última compra…). Si una semana no hay nada para ofrecerle, ese mail no sale. Cuando compra, deja de recibirlos.
          </p>
        </div>
        <div style={st.acciones}>
          <span style={{ ...sx.badge, ...(activa ? { background: colors.okBg, color: colors.ok } : { background: colors.bgSoft, color: colors.textSub }) }}>
            {activa ? `Prendida desde ${fmtFecha(r.activo_desde)}` : 'Apagada'}
          </span>
          {esAdmin && !confirmar && (
            <Boton variante={activa ? 'secondary' : 'primary'} onClick={() => setConfirmar(true)} disabled={ocupado}>
              {activa ? 'Apagar' : 'Prender'}
            </Boton>
          )}
        </div>
      </div>

      {confirmar && (
        <div style={st.confirmar}>
          <span>
            {activa
              ? '¿Apagar la Recompra? No entra nadie nuevo y no sale ningún mail más hasta que la vuelvas a prender.'
              : `¿Prender la Recompra? Desde mañana a la mañana entran hasta ${r?.cupo_diario ?? 50} clientes por día y los mails salen de 18 a 21 h.`}
          </span>
          <Boton variante="primary" sm onClick={() => configurar({ activo: !activa }, activa ? 'La Recompra quedó apagada.' : 'La Recompra quedó prendida: mañana entran los primeros clientes.')} disabled={ocupado}>
            {activa ? 'Sí, apagar' : 'Sí, prender'}
          </Boton>
          <Boton variante="ghost" sm onClick={() => setConfirmar(false)}>Cancelar</Boton>
        </div>
      )}
      {aviso && <p style={{ ...st.aviso, color: aviso.ok ? colors.ok : colors.err }}>{aviso.texto}</p>}
      {r?.freno && activa && <p style={{ ...st.aviso, color: colors.warn }}>Hoy no entran clientes nuevos: {r.freno}.</p>}

      {/* ── Los números ── */}
      <div style={st.kpis}>
        <Kpi label="Mails enviados" valor={t?.mails ?? 0} detalle={`${t?.mails_hoy ?? 0} hoy · ${mails30} en 30 días`} />
        <Kpi label="Clientes que recibieron" valor={t?.clientes ?? 0} detalle={`${r?.activas ?? 0} recibiendo ahora · ${r?.sin_respuesta ?? 0} terminaron sin comprar`} />
        <Kpi destacado label="Ventas generadas" valor={t?.ventas ?? 0} detalle={`${pesos(t?.monto ?? 0)} · compraron dentro de los 14 días de un mail`} />
        <Kpi label="Abrieron · entraron" valor={tasa(t?.abiertos ?? 0, t?.mails ?? 0)} detalle={`${tasa(t?.clicks ?? 0, t?.mails ?? 0)} entró a la tienda`} />
        <Kpi label="Volvieron a comprar" valor={pct(r?.tratados?.tasa)}
          detalle={`con mails · sin mails (control): ${pct(r?.control?.tasa)}`} />
      </div>

      {/* ── Mails por día ── */}
      <div style={st.panel}>
        <div style={st.panelHead}>
          <div style={st.kpiLabel}>Mails por día · últimos 30 días</div>
          <div style={st.leyenda}>
            <span><i style={{ ...st.cuadrito, background: colors.info }} /> mails enviados</span>
            <span><i style={{ ...st.cuadrito, background: colors.ok }} /> ventas</span>
          </div>
        </div>
        <div style={st.graficoWrap}>
          <div style={st.grafico}>
            {diario.map(d => (
              <div key={d.fecha} style={st.colDia}
                title={`${fmtFecha(d.fecha)}: ${d.mails} mails · ${d.abiertos} abrieron · ${d.clicks} entraron · ${d.ventas} ventas (${pesos(d.monto)})`}>
                <div style={st.ventasDia}>{d.ventas > 0 ? d.ventas : ''}</div>
                <div style={st.barraZona}>
                  <div style={{ ...st.barra, height: `${(d.mails / maxDia) * 100}%` }}>
                    {d.ventas > 0 && <div style={st.barraVenta} />}
                  </div>
                </div>
                <div style={st.numDia}>{d.mails || ''}</div>
                <div style={st.fechaDia}>{diaCorto(d.fecha)}</div>
              </div>
            ))}
          </div>
        </div>
        {mails30 === 0 && <p style={st.vacioChico}>Todavía no salió ningún mail.</p>}
      </div>

      {/* ── Cuántos mails recibió cada cliente ── */}
      <div style={st.panel}>
        <div style={st.kpiLabel}>Cuántos mails recibió cada cliente</div>
        <table style={{ ...sx.table, marginTop: space.sm }}>
          <thead>
            <tr>
              <th style={st.thChico}>Mails recibidos</th>
              <th style={{ ...st.thChico, width: '45%' }}>Clientes</th>
              <th style={st.thChico}>Ventas tras ese mail</th>
            </tr>
          </thead>
          <tbody>
            {embudo.map(e => (
              <tr key={e.mails}>
                <td style={st.tdChico}>{e.mails === 1 ? '1 o más' : `${e.mails} o más`}</td>
                <td style={st.tdChico}>
                  <div style={st.embudoFila}>
                    <div style={st.embudoPista}>
                      <div style={{ ...st.embudoBarra, width: base ? `${(e.clientes / base) * 100}%` : 0 }} />
                    </div>
                    <span style={sx.mono}>{e.clientes}</span>
                  </div>
                </td>
                <td style={st.tdChico}>
                  <span style={sx.mono}>{e.ventas}</span>
                  {e.ventas > 0 && <span style={st.montoChico}>{pesos(e.monto)}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={st.tablas}>
        <Embudo titulo="Por tipo de mail" filas={r?.por_senal} etiqueta={k => senales[k] ?? k} />
        <Embudo titulo="Por día de la semana" filas={r?.por_dia} etiqueta={k => DIAS[k] ?? k} />
        <Embudo titulo="Por lo que compra" filas={r?.por_familia} etiqueta={k => familias[k] ?? k} />
      </div>

      {/* ── Los clientes ── */}
      <div style={st.seccion}>
        <div style={st.kpiLabel}>Los clientes</div>
      </div>
      <div style={sx.filterBar}>
        {VISTAS.map(v => (
          <button key={v.id} type="button" onClick={() => setVista(v.id)}
            style={{ ...sx.pill, ...(vista === v.id ? sx.pillActive : {}) }}>{v.label}</button>
        ))}
        <select value={familiaFiltro} onChange={e => setFamiliaFiltro(e.target.value)} style={st.select}>
          <option value="">Compran de todo</option>
          {Object.entries(familias).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {error && <p style={st.error}>No se pudo actualizar el listado. Se reintenta solo en un minuto.</p>}

      <div style={st.tableWrap}>
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Cliente</th>
              <th style={sx.th}>Compra</th>
              <th style={sx.th}>Última compra</th>
              <th style={sx.th}>Mails recibidos</th>
              <th style={sx.th}>Último mail</th>
              <th style={sx.th}>Abrió · Entró</th>
              <th style={sx.th}>Ventas</th>
              <th style={sx.th}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {!data && !error && <tr><td colSpan={8} style={st.vacio}>Cargando…</td></tr>}
            {data && filas.length === 0 && (
              <tr><td colSpan={8} style={st.vacio}>
                {activa ? 'Todavía no hay clientes en este filtro.' : 'La Recompra está apagada. Cuando la prendas, acá vas a ver a cada cliente y los mails que recibe.'}
              </td></tr>
            )}
            {filas.map(f => {
              const a = f.actividad ?? {}
              const est = ESTADO[f.estado] ?? ESTADO.activa
              return (
                <tr key={f.id} style={st.fila}>
                  <td style={st.td}>
                    <div style={st.nombre}>
                      {f.cliente.nombre || 'Sin nombre en la ficha'}
                      {f.grupo_control && <span style={st.control} title="Grupo de control: no recibe mails, se mide igual">control</span>}
                    </div>
                    <div style={st.subLinea}>{f.email} · entró {fmtFecha(f.inscripta_el)}</div>
                  </td>
                  <td style={st.td}>{familias[f.familia] ?? f.familia}</td>
                  <td style={st.td}><span style={sx.mono}>{f.ultima_compra ? fmtFecha(f.ultima_compra) : '—'}</span></td>
                  <td style={st.td}>
                    {f.grupo_control
                      ? <span style={st.nada}>no recibe</span>
                      : <>
                        <span style={sx.mono}>{a.mails ?? 0} de 10</span>
                        {f.estado === 'activa' && f.proximo_contacto_el && <div style={st.subLinea}>próximo {fmtFecha(f.proximo_contacto_el)}</div>}
                      </>}
                  </td>
                  <td style={st.td}>
                    {a.ultimo
                      ? <><span style={sx.mono}>{fmtFecha(a.ultimo.fecha)}</span><div style={st.subLinea}>{senales[a.ultimo.senal] ?? a.ultimo.asunto}</div></>
                      : <span style={st.nada}>—</span>}
                  </td>
                  <td style={st.td}>
                    {a.mails ? <span>{a.abrio ? 'Sí' : 'No'} · {a.entro ? 'Sí' : 'No'}</span> : <span style={st.nada}>—</span>}
                  </td>
                  <td style={st.td}>
                    {a.ventas ? <><span style={{ ...sx.mono, color: colors.ok, fontWeight: 600 }}>{pesos(a.monto)}</span><div style={st.subLinea}>{a.ventas} {a.ventas === 1 ? 'venta' : 'ventas'}</div></> : <span style={st.nada}>—</span>}
                  </td>
                  <td style={st.td}>
                    <span style={{ ...sx.badge, background: est.bg, color: est.fg }}>{est.label}</span>
                    {f.volvio_por && <div style={st.subLinea}>{f.volvio_por}</div>}
                    {f.motivo && f.estado !== 'volvio' && <div style={st.subLinea}>{f.motivo}</div>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ── Ver los mails antes de mandarlos ── */}
      <div style={st.seccion}>
        <div style={st.kpiLabel}>Ver los mails antes de mandarlos</div>
      </div>
      <div style={st.plantillas}>
        <div style={st.plantillasCtl}>
          <div style={st.ayuda}>
            Así le llegarían hoy los mails a un cliente real, uno por cada cosa que tenemos para ofrecerle.
            Poné un código de cliente o dejalo vacío y elegimos uno.
          </div>
          <form style={st.clienteForm} onSubmit={e => { e.preventDefault(); setClienteBuscado(prevCliente.trim()) }}>
            <input id="recompra-cliente" value={prevCliente} onChange={e => setPrevCliente(e.target.value)}
              placeholder="Código de cliente" style={st.clienteInput} inputMode="numeric" />
            <Boton variante="secondary" sm type="submit">Ver</Boton>
          </form>
          {preview?.cliente && <div style={st.subLinea}>{preview.cliente.nombre} · {preview.cliente.codigo_presea}</div>}

          <div style={st.kpiLabel}>Diseño del mail</div>
          <div style={st.numeros}>
            {Object.entries(estilos).map(([k, v]) => (
              <button key={k} type="button" onClick={() => setPrevEstilo(k)}
                style={{ ...sx.pill, ...(estiloVista === k ? sx.pillActive : {}) }}>
                {v}{r?.estilo === k ? ' ✓' : ''}
              </button>
            ))}
          </div>
          <div style={st.subLinea}>El que tiene ✓ es el que se manda.</div>
          {esAdmin && r?.estilo !== estiloVista && (
            <Boton variante="primary" sm disabled={ocupado}
              onClick={() => configurar({ estilo: estiloVista }, `Desde ahora los mails salen con el diseño ${estilos[estiloVista]}.`)}>
              Mandar con este diseño
            </Boton>
          )}

          <div style={st.kpiLabel}>Quién firma la carta</div>
          <div style={st.ayuda}>Uno de los mails es una carta de una persona de Ventas. Si el cliente responde, le llega a esa persona.</div>
          {r?.firma
            ? <div style={st.subLinea}>{r.firma.nombre} · {r.firma.email}</div>
            : <div style={{ ...st.subLinea, color: colors.warn }}>Sin firma, la carta no sale (los demás mails sí).</div>}
          {esAdmin && (
            <div style={st.firma}>
              <input id="recompra-firma-nombre" value={firmaNombre ?? r?.firma?.nombre ?? ''} onChange={e => setFirmaNombre(e.target.value)} placeholder="Nombre" style={st.firmaInput} />
              <input id="recompra-firma-email" value={firmaEmail ?? r?.firma?.email ?? ''} onChange={e => setFirmaEmail(e.target.value)} placeholder="Email" style={st.firmaInput} />
              <Boton variante="secondary" sm disabled={ocupado || (firmaNombre === null && firmaEmail === null)}
                onClick={() => configurar({ firma_nombre: firmaNombre ?? r?.firma?.nombre ?? '', firma_email: firmaEmail ?? r?.firma?.email ?? '' }, 'La firma de la carta quedó guardada.').then(() => { setFirmaNombre(null); setFirmaEmail(null) })}>
                Guardar firma
              </Boton>
            </div>
          )}

          <Boton variante="secondary" sm onClick={muestra} disabled={ocupado || !preview?.senales?.length}
            title="Te llegan a tu mail. A ningún cliente le llega nada.">
            Mandármelos a mi mail
          </Boton>

          {esAdmin && (
            <>
              <div style={st.kpiLabel}>Clientes nuevos por día</div>
              <div style={st.cupoEdit}>
                <input type="number" min="0" max="1000" value={cupo} placeholder={String(r?.cupo_diario ?? '')}
                  onChange={e => setCupo(e.target.value)} style={st.cupoInput} />
                <Boton variante="secondary" sm disabled={ocupado || cupo === ''}
                  onClick={() => configurar({ cupo_diario: Number(cupo) }, `Desde mañana entran hasta ${cupo} clientes por día.`).then(() => setCupo(''))}>
                  Cambiar
                </Boton>
              </div>
              <div style={st.subLinea}>Hoy: {r?.cupo_diario ?? '—'} por día{activa ? ` · pueden entrar ${r?.cupo_hoy ?? 0}` : ''}</div>
            </>
          )}
        </div>
        <div style={st.previewCol}>
          {preview?.senales?.length > 0 && (
            <div style={st.numeros}>
              {preview.senales.map((m, n) => (
                <button key={m.senal} type="button" onClick={() => setSenalVista(n)}
                  style={{ ...sx.pill, ...(senalVista === n ? sx.pillActive : {}) }}>{m.label}</button>
              ))}
            </div>
          )}
          {preview?.senales?.[senalVista] && <div style={st.asunto}><span style={st.kpiLabel}>Asunto</span> {preview.senales[senalVista].asunto}</div>}
          <div style={st.previewWrap}>
            {prevError && <p style={st.vacioChico}>{prevError}</p>}
            {!prevError && !preview && <p style={st.vacioChico}>Armando los mails…</p>}
            {preview && preview.senales.length === 0 && <p style={st.vacioChico}>Este cliente hoy no tiene nada para ofrecerle: no le saldría ningún mail.</p>}
            {preview?.senales?.[senalVista] && <iframe title="Vista previa del mail" srcDoc={preview.senales[senalVista].html} style={st.iframe} sandbox="" />}
          </div>
        </div>
      </div>
    </div>
  )
}

function Kpi({ label, valor, detalle, destacado = false }) {
  return (
    <div style={{ ...st.kpi, ...(destacado ? st.kpiDestacado : {}) }}>
      <div style={st.kpiLabel}>{label}</div>
      <div style={{ ...st.kpiValor, ...(destacado ? { color: colors.ok } : {}) }}>{valor}</div>
      <div style={st.kpiDetalle}>{detalle}</div>
    </div>
  )
}

function Embudo({ titulo, filas, etiqueta }) {
  return (
    <div style={st.embudo}>
      <div style={st.kpiLabel}>{titulo}</div>
      <table style={{ ...sx.table, marginTop: space.xs }}>
        <thead>
          <tr>
            <th style={st.thChico} />
            <th style={st.thChico}>Mails</th><th style={st.thChico}>Abrió</th>
            <th style={st.thChico}>Entró</th><th style={st.thChico}>Ventas</th>
          </tr>
        </thead>
        <tbody>
          {(filas ?? []).length === 0 && <tr><td colSpan={5} style={st.vacioChico}>Todavía no salió ningún mail.</td></tr>}
          {(filas ?? []).map(m => (
            <tr key={m.clave}>
              <td style={st.tdChico}>{etiqueta(m.clave)}</td>
              <td style={st.tdChico}><span style={sx.mono}>{m.enviados}</span></td>
              <td style={st.tdChico}><span style={sx.mono}>{tasa(m.abiertos, m.enviados)}</span></td>
              <td style={st.tdChico}><span style={sx.mono}>{tasa(m.clicks, m.enviados)}</span></td>
              <td style={st.tdChico}><span style={sx.mono}>{m.compras}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.lg, marginBottom: space.lg, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  sub: { margin: `${space.xs}px 0 0`, fontSize: text.sec, color: colors.textSec, maxWidth: 760 },
  acciones: { display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap' },
  confirmar: { display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap', padding: `${space.md}px ${space.lg}px`, background: colors.warnBg, border: `1px solid ${colors.border}`, borderRadius: radius.md, marginBottom: space.md, fontSize: text.body },
  aviso: { fontSize: text.sec, margin: `0 0 ${space.md}px` },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))', gap: space.md, marginBottom: space.lg },
  kpi: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, minWidth: 0 },
  kpiDestacado: { borderColor: colors.ok, background: colors.okSoft },
  kpiLabel: { fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  kpiValor: { ...sx.mono, fontSize: 22, fontWeight: 600, marginTop: space.xs, color: colors.text },
  kpiDetalle: { fontSize: text.sec, color: colors.textSec, marginTop: 2 },
  panel: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg, minWidth: 0 },
  panelHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md, flexWrap: 'wrap' },
  leyenda: { display: 'flex', gap: space.lg, fontSize: text.sec, color: colors.textSec },
  cuadrito: { display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 4, verticalAlign: 'middle' },
  graficoWrap: { overflowX: 'auto', marginTop: space.md },
  grafico: { display: 'grid', gridTemplateColumns: 'repeat(30, minmax(22px, 1fr))', gap: 4, minWidth: 720, alignItems: 'end' },
  colDia: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, cursor: 'default' },
  ventasDia: { fontSize: text.meta, fontWeight: 700, color: colors.ok, height: 14 },
  barraZona: { height: 120, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: colors.bgSoft, borderRadius: 3 },
  barra: { width: '70%', background: colors.info, borderRadius: '3px 3px 0 0', minHeight: 0, position: 'relative' },
  barraVenta: { position: 'absolute', top: -3, left: 0, right: 0, height: 4, background: colors.ok, borderRadius: 2 },
  numDia: { ...sx.mono, fontSize: text.meta, color: colors.text, height: 14 },
  fechaDia: { fontSize: 10, color: colors.textMeta, whiteSpace: 'nowrap' },
  embudoFila: { display: 'flex', alignItems: 'center', gap: space.md },
  embudoPista: { flex: 1, height: 10, background: colors.bgSoft, borderRadius: 5, overflow: 'hidden', minWidth: 60 },
  embudoBarra: { height: '100%', background: colors.info, borderRadius: 5 },
  montoChico: { marginLeft: space.sm, fontSize: text.sec, color: colors.ok },
  tablas: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: space.md, marginBottom: space.lg },
  embudo: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.md}px ${space.lg}px`, minWidth: 0, overflowX: 'auto' },
  thChico: { ...sx.th, height: 28, fontSize: text.meta, padding: `0 ${space.sm}px` },
  tdChico: { ...sx.td, height: 30, fontSize: text.sec, whiteSpace: 'nowrap', padding: `0 ${space.sm}px` },
  vacioChico: { ...sx.td, height: 40, fontSize: text.sec, color: colors.textMeta, textAlign: 'center' },
  seccion: { margin: `${space['2xl']}px 0 ${space.md}px`, paddingTop: space.md, borderTop: `1px solid ${colors.border}` },
  // flex-wrap en vez de grid fija: bajo ~700 px la vista previa baja debajo de los controles en vez de apretarse
  plantillas: { display: 'flex', flexWrap: 'wrap', gap: space.md, marginBottom: space.lg, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: space.lg },
  plantillasCtl: { flex: '1 1 220px', maxWidth: 300, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.md, alignItems: 'flex-start' },
  numeros: { display: 'flex', flexWrap: 'wrap', gap: space.xs },
  asunto: { fontSize: text.body, color: colors.text },
  select: { ...sx.input, width: 'auto' },
  previewCol: { flex: '999 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.sm },
  previewWrap: { minWidth: 0, border: `1px solid ${colors.borderSoft}`, borderRadius: radius.md, overflow: 'hidden', minHeight: 420, background: '#f5f5f3' },
  ayuda: { fontSize: text.sec, color: colors.textSec },
  clienteForm: { display: 'flex', gap: space.sm, alignItems: 'center' },
  firma: { display: 'flex', flexDirection: 'column', gap: space.xs, alignSelf: 'stretch' },
  firmaInput: { ...sx.input, width: '100%' },
  cupoEdit: { display: 'flex', gap: space.sm, alignItems: 'center' },
  cupoInput: { ...sx.input, width: 90 },
  clienteInput: { ...sx.input, width: 180 },
  iframe: { width: '100%', height: 760, border: 0, display: 'block' },
  error: { fontSize: text.sec, color: colors.err, margin: `0 0 ${space.md}px` },
  tableWrap: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflowX: 'auto' },
  fila: { height: 56, borderBottom: `1px solid ${colors.borderSoft}` },
  vacio: { ...sx.td, height: 64, textAlign: 'center', color: colors.textSec },
  td: { ...sx.td, padding: `${space.sm}px ${space.lg}px`, whiteSpace: 'nowrap' },
  nombre: { fontWeight: 500 },
  control: { ...sx.badge, marginLeft: space.sm, background: colors.bgSoft, color: colors.textSec, border: `1px solid ${colors.border}` },
  subLinea: { fontSize: text.sec, color: colors.textMeta, marginTop: 2 },
  nada: { color: colors.textMeta, fontSize: text.sec },
}
